import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchHomeMembers,
  fetchProfile,
  signOut,
  type HomeMember,
  type Profile,
} from "../lib/auth";
import { fetchMyHouseholds } from "../lib/households";
import { supabase } from "../lib/supabase";
import { BackIcon } from "../components/icons";

export default function SettingsScreen() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [email, setEmail] = useState("");
  const [members, setMembers] = useState<HomeMember[]>([]);

  const load = useCallback(async () => {
    try {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) return;
      setEmail(auth.user.email || "");

      const p = await fetchProfile(auth.user.id);
      setProfile(p);

      const homes = await fetchMyHouseholds();
      const first = homes[0];
      if (first) {
        setMembers(await fetchHomeMembers(first.id));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function handleSignOut() {
    if (confirm("Sign out? You'll need to sign in again.")) {
      signOut();
    }
  }

  if (loading) return <div className="loading-screen">Loading…</div>;

  const initial = (profile?.display_name || email || "?")
    .charAt(0)
    .toUpperCase();

  return (
    <div className="app-shell">
      <div className="header-row">
        <button type="button" className="icon-btn" onClick={() => navigate(-1)}>
          <BackIcon />
        </button>
        <h1 className="header-title">Settings</h1>
        <div style={{ width: 40, height: 40 }} />
      </div>

      <div className="settings-hero">
        {profile?.avatar_url ? (
          <img
            src={profile.avatar_url}
            alt=""
            className="settings-avatar"
          />
        ) : (
          <div className="settings-avatar settings-avatar--fallback">
            {initial}
          </div>
        )}
        <div className="settings-name">
          {profile?.display_name || "Unknown"}
        </div>
        <div className="settings-email">{email}</div>
      </div>

      <div className="card settings-section">
        <div className="section-label">PROFILE</div>
        <div className="settings-row">
          <span className="settings-row__label">Display name</span>
          <span className="settings-row__value">
            {profile?.display_name || "—"}
          </span>
        </div>
        <div className="settings-divider" />
        <div className="settings-row">
          <span className="settings-row__label">Phone</span>
          <span className="settings-row__value">
            {profile?.phone_number || "Not set"}
          </span>
        </div>
        <button
          type="button"
          className="secondary-btn"
          onClick={() => navigate("/settings/profile")}
          style={{ marginTop: 4 }}
        >
          Edit profile
        </button>
      </div>

      <div className="card settings-section">
        <div className="section-label">MEMBERS ({members.length})</div>
        {members.map((m, i) => (
          <div key={m.user_id}>
            {i > 0 && <div className="settings-divider" />}
            <div className="member-row">
              {m.avatar_url ? (
                <img
                  src={m.avatar_url}
                  alt=""
                  className="member-avatar"
                />
              ) : (
                <div className="member-avatar member-avatar--fallback">
                  {(m.display_name || "?").charAt(0).toUpperCase()}
                </div>
              )}
              <div style={{ flex: 1 }}>
                <div className="member-name">
                  {m.display_name || "Unknown"}
                </div>
                {m.phone_number && (
                  <div className="member-meta">{m.phone_number}</div>
                )}
              </div>
            </div>
          </div>
        ))}
        <button
          type="button"
          className="secondary-btn"
          onClick={() => navigate("/housemates")}
          style={{ marginTop: 4 }}
        >
          View housemates
        </button>
      </div>

      <button
        type="button"
        className="danger-btn"
        onClick={handleSignOut}
        style={{ marginTop: 8 }}
      >
        Sign out
      </button>

      <style>{`
        .settings-hero {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          padding: 12px 0;
        }
        .settings-avatar {
          width: 88px;
          height: 88px;
          border-radius: 44px;
          object-fit: cover;
        }
        .settings-avatar--fallback {
          background: #E8F5EE;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 34px;
          font-weight: 700;
          color: #2D7A4F;
        }
        .settings-name {
          font-family: Georgia, serif;
          font-size: 22px;
          font-weight: 700;
          color: #1f5a39;
          margin-top: 6px;
        }
        .settings-email { font-size: 13px; color: #6b7a6e; }
        .settings-section { display: flex; flex-direction: column; gap: 12px; }
        .settings-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 4px 0;
          gap: 12px;
        }
        .settings-row__label { font-size: 14px; color: #6b7a6e; }
        .settings-row__value {
          font-size: 14px;
          color: #1a2e22;
          font-weight: 600;
          text-align: right;
        }
        .settings-divider {
          height: 1px;
          background: #eceff0;
        }
        .member-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 8px 0;
        }
        .member-avatar {
          width: 40px;
          height: 40px;
          border-radius: 20px;
          object-fit: cover;
        }
        .member-avatar--fallback {
          background: #E8F5EE;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 15px;
          font-weight: 700;
          color: #2D7A4F;
        }
        .member-name { font-size: 14px; font-weight: 700; color: #1a2e22; }
        .member-meta { font-size: 12px; color: #6b7a6e; margin-top: 2px; }
      `}</style>
    </div>
  );
}
